import { usePermissions, useTranslate } from "../../../../customHooks";
import { useMemo, useCallback, useRef, useEffect, useState } from "react";
import { FormLayout, Input, Checkbox, RadioGroup, SubmitButton } from "@panwds/react-form";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import { isValidIPAMPoolId } from "../../../../utils/validate";
import { Banner, Button, LoadingPanel, Subtitle, Link } from "@panwds/react-ui";
import { useUpdateFirewallMutation } from "../../../../redux/services/firewalls-service";
import { useSyncIPAMPoolMutation, useGetBYOIPSettingsQuery } from "../../../../redux/services/settings-service";
import { extractFieldsWithConfig } from "../../../../utils/utils";
import { isEmpty } from "lodash";
import AWSServiceIPsTableComponent from "../Edit/EgressNatComponents/AWSServiceIPsTableComponent";
import { toast } from "../../../../components";
import { FirewallUpdateBanner } from "../../FirewallUpdateBanner";
import fwStyles from "../../FirewallEdit.module.scss";
import TabPageHeader from "./TabPageHeader";
import { determinePolicyManaged, POLICY_MANAGER, isFirewallUpdating } from "../../firewallsUtil";
import { SyncIcon, CircleCheckIcon, ExternalLinkIcon } from '@panwds/icons';

const FirewallEgressNat = (props: { canEdit: boolean, firewallData: Record<string, any>; isLoading: boolean; refreshFirewallData: () => void; featureFlags?: Record<string, any> }) => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const [updateFirewall] = useUpdateFirewallMutation();
    const [syncIPAMPool, { isLoading: isSyncing }] = useSyncIPAMPoolMutation();
    const region = useMemo(() => new URLSearchParams(location.search).get('region'), [location.search]);
    const byoipSyncEnabled = props?.featureFlags?.BYOIPSync === true;
    const { data: byoipSettings, refetch: refetchSettings } = useGetBYOIPSettingsQuery(
        { region: region || '' },
        { skip: !byoipSyncEnabled }
    );

    // Format timestamp to remove microseconds and show timezone
    const formatTimestamp = (timestamp: string | undefined) => {
        if (!timestamp) return null;
        try {
            // Timestamp is already in "YYYY-MM-DD HH:MM:SS.microseconds" UTC format
            // Just remove microseconds and add UTC suffix
            const withoutMicroseconds = timestamp.split('.')[0];
            return `${withoutMicroseconds} UTC`;
        } catch {
            return timestamp;
        }
    };

    // Sync state
    const [syncResult, setSyncResult] = useState<{
        newIPs: number;
        totalIPs: number;
        updatedDateTime?: string;
    } | null>(null);
    const firewallUpdating = isFirewallUpdating(props?.firewallData?.Status?.FirewallStatus);
    const policyMangedBy = determinePolicyManaged(props?.firewallData);
    const egressNatSCMEnabled = props?.featureFlags?.EgressNatSCM === true;
    // Check if egress NAT is currently enabled in the firewall
    const isEgressNatCurrentlyEnabled = props?.firewallData?.Firewall?.EgressNAT?.Enabled === true;
    // Disable IP pool settings when feature flag is off (allow only toggling checkbox)
    const disableIPPoolSettings = policyMangedBy === POLICY_MANAGER.STRATA && !egressNatSCMEnabled;

    const config = { Firewall: ["FirewallId", "Region", "EgressNAT", "UpdateToken", "DeploymentUpdateToken"], Status: ["PublicIPs"] };

    const transformFormDefaultValue = useMemo(() => {
        if (isEmpty(props?.firewallData)) {
            return undefined;
        }

        const newStateWithConfig: any = extractFieldsWithConfig(props?.firewallData, config);

        const updatedFirewall = {
            ...newStateWithConfig.Firewall,
            EgressNAT: {
                ...newStateWithConfig.Firewall?.EgressNAT,
                Settings: {
                    ...newStateWithConfig.Firewall?.EgressNAT?.Settings,
                    IPPoolType: newStateWithConfig.Firewall?.EgressNAT?.Settings?.IPPoolType || 'AWSService',
                    IPAMPoolId: newStateWithConfig.Firewall?.EgressNAT?.Settings?.IPAMPoolId
                }
            },
        };

        return {
            Firewall: updatedFirewall,
            Status: {
                ...newStateWithConfig?.Status,
            }
        };
    }, [props?.firewallData]);

    const formMethods = useForm({
        defaultValues: transformFormDefaultValue,
        mode: 'onBlur',
    });

    const { watch, formState: { errors, dirtyFields }, reset } = formMethods;
    const egressNatEnable = watch("Firewall.EgressNAT.Enabled");
    const iPoolType = useWatch({ control: formMethods.control, name: "Firewall.EgressNAT.Settings.IPPoolType" });
    const currentIPAMPoolId = useWatch({ control: formMethods.control, name: "Firewall.EgressNAT.Settings.IPAMPoolId" });
    // Ref to store the initial IPPoolType value
    const initialIPPoolTypeRef = useRef(formMethods.getValues("Firewall.EgressNAT.Settings.IPPoolType"));
    // Check if egress NAT was initially enabled in the API response
    const wasEgressNatInitiallyEnabled = props?.firewallData?.Firewall?.EgressNAT?.Enabled === true;

    useEffect(() => {
        initialIPPoolTypeRef.current = formMethods.getValues("Firewall.EgressNAT.Settings.IPPoolType");
        // Reset form when firewall data is refreshed
        if (transformFormDefaultValue) {
            reset(transformFormDefaultValue);
        }
        // Clear sync result when firewall data changes
        setSyncResult(null);
    }, [transformFormDefaultValue, reset]);

    // Clear sync result when IPAM Pool ID changes
    useEffect(() => {
        setSyncResult(null);
    }, [currentIPAMPoolId]);

    const transformFormSubmitValue = useCallback((formData: Record<string, any>) => {
        if (isEmpty(formData)) {
            return;
        }

        const { Firewall, Status } = formData;

        let transformedData = {
            ...Firewall,
            Region: region,
            EgressNAT: {
                ...Firewall?.EgressNAT
            }
        };

        if (Status && Status?.PublicIPs) {
            transformedData.Status = {
                PublicIPs: Status?.PublicIPs,
            };
        }

        if (!Firewall?.EgressNAT?.Enabled) {
            delete transformedData?.EgressNAT?.Settings;
            delete transformedData?.Status;
        } else if (Firewall?.EgressNAT?.Settings?.IPPoolType === 'AWSService') {
            delete transformedData?.EgressNAT?.Settings?.IPAMPoolId;
        } else if (transformedData?.Status === undefined || Firewall?.EgressNAT?.Settings?.IPPoolType !== 'AWSService') {
            delete transformedData?.Status;
        }

        return transformedData;
    }, [region]);

    const onSubmit = useCallback(async (data) => {
        try {
            const result = await updateFirewall({
                payload: transformFormSubmitValue(data)
            }).unwrap();

            // Refetch the latest firewall data
            await props.refreshFirewallData();

            toast.success("Firewall updated successfully");
        } catch (error: any) {
            if(error?.code === 409) {
                await props.refreshFirewallData();
            }
            toast.error(error?.error || error?.message || 'Something went wrong', { toastId: "update-firewall-egress-nat" });
        }
    }, [updateFirewall, transformFormSubmitValue, region, props.refreshFirewallData, reset, transformFormDefaultValue]);

    const handleCancel = () => {
        reset(transformFormDefaultValue);
    };

    const handleSyncIPAMPool = async () => {
        if (!byoipSyncEnabled) {
            toast.error('BYOIP Sync feature is not enabled');
            return;
        }

        const ipamPoolId = formMethods.getValues('Firewall.EgressNAT.Settings.IPAMPoolId');

        if (!ipamPoolId) {
            toast.error('IPAM Pool ID is required for sync');
            return;
        }

        if (!region) {
            toast.error('Region is required for IPAM Pool sync');
            return;
        }

        try {
            const result = await syncIPAMPool({ ipamPoolId, region }).unwrap();

            // Extract pool data
            const poolData = result.PublicIPPools?.[ipamPoolId];
            if (!poolData) {
                toast.error('No pool data returned from sync');
                return;
            }

            const totalIPs = poolData.DeploymentIPv4Pool.TotalIPCount || 0;
            const lastSyncTime = poolData.LastSyncTimeUTC;
            const allocations = poolData.DeploymentIPv4Pool.Allocations || {};

            // Parse timestamp in format "YYYY-MM-DD HH:MM:SS.microseconds" to milliseconds
            const parseTimestamp = (ts: string) => {
                // Replace space with 'T' and add 'Z' to indicate UTC
                const isoFormat = ts.replace(' ', 'T') + 'Z';
                return new Date(isoFormat).getTime();
            };

            // Calculate new IPs by checking if allocation time matches last sync time
            let newIPs = 0;
            if (lastSyncTime) {
                const syncTimeMs = parseTimestamp(lastSyncTime);

                Object.values(allocations).forEach((allocation) => {
                    const allocationTimeMs = parseTimestamp(allocation.AllocationTimeUTC);
                    const timeDiff = Math.abs(allocationTimeMs - syncTimeMs);

                    // If allocation time is within 1 minute of sync time, consider it new
                    if (timeDiff < 60 * 1000) {
                        newIPs += allocation.IPCount;
                    }
                });
            }

            setSyncResult({
                newIPs,
                totalIPs,
                updatedDateTime: lastSyncTime,
            });
            // Refetch settings to get updated LastSyncTimeUTC
            await refetchSettings();
        } catch (error: any) {
            const uniqueToastId = `sync-ipam-pool-${Date.now()}`;
            toast.error(`${error?.code}: ${error?.error}`, {
                toastId: uniqueToastId,
            });
        }
    };

    const awsServiceOptions = [
        { value: 'AWSService', label: translate('resources.firewallsV2.awsService') },
        { value: 'BYOIP', label: translate('resources.firewallsV2.byoip') }
    ];

    // Check if IPAM Pool ID is saved (not dirty and no errors)
    const isIPAMPoolIdSaved =
        currentIPAMPoolId &&
        !dirtyFields?.Firewall?.EgressNAT?.Settings?.IPAMPoolId &&
        !errors?.Firewall?.EgressNAT?.Settings?.IPAMPoolId &&
        !isValidIPAMPoolId(currentIPAMPoolId);

    const renderPolicyManagedBanner = () => {
        if(policyMangedBy === POLICY_MANAGER.STRATA && !egressNatSCMEnabled && !isEgressNatCurrentlyEnabled) {
            return (
                <Banner type="inline" appearance="standard" showIcon>
                    {translate('resources.firewallsV2.userid.policyManagedEgressNatByBanner')}
                </Banner>
            );
        }
    }

    return (
        <div className="tw-relative">
            {props?.isLoading &&
                <div className="tw-absolute tw-inset-0 tw-right-0 tw-flex tw-justify-center tw-items-center tw-backdrop-blur-sm tw-z-50" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                    <div className="tw-w-6 tw-h-6">
                        <LoadingPanel />
                    </div>
                </div>
            }
            {!props?.canEdit &&
                <FirewallUpdateBanner content={translate('resources.firewallsV2.cantEditFirewall')} />
            }
            {firewallUpdating &&
                <FirewallUpdateBanner refresh={props.refreshFirewallData} />
            }
            <TabPageHeader title={translate(`resources.firewallsV2.egressNat`)} addClassName="tw-pb-4" />
            <div className={(!props?.canEdit || firewallUpdating || (policyMangedBy === POLICY_MANAGER.STRATA && !egressNatSCMEnabled && !isEgressNatCurrentlyEnabled) || !permissions?.UpdateFirewall) ? fwStyles.fwReadOnly : ""}>
                {renderPolicyManagedBanner()}
                <FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                        <FormLayout>
                            <Checkbox
                                name="Firewall.EgressNAT.Enabled"
                                label={translate(`resources.firewallsV2.enableEgressNat`)}
                                dataMetrics={"cloudngfw-firewall-edit-egressNatEnabled"}
                                description={translate(`resources.firewallsV2.enableEgressNatMutedText`)}
                            />

                            {egressNatEnable && (
                                <>
                                    <Subtitle as={"div"} appearance={"semibold"} size={"sm"}>
                                        {translate(`resources.firewallsV2.ipAddressPool`)}
                                    </Subtitle>
                                    <RadioGroup
                                        name="Firewall.EgressNAT.Settings.IPPoolType"
                                        horizontal={true}
                                        disabled={disableIPPoolSettings}
                                        items={awsServiceOptions?.map(awsService => ({
                                            value: awsService?.value,
                                            children: awsService?.label
                                        }))}
                                    />
                                    {iPoolType === 'BYOIP' && (
                                        <>
                                            {/* Input field with optional Sync button */}
                                            <div className="tw-flex tw-gap-2">
                                                <div style={{ flex: '1 1 0%', maxWidth: '1000px' }}>
                                                    <Input
                                                        name="Firewall.EgressNAT.Settings.IPAMPoolId"
                                                        label={translate(`resources.firewallsV2.ipAddressPool`)}
                                                        placeholder="Please enter a IPAM pool id (e.g., ipam-pool-0123456789abcdef)"
                                                        register={{ validate: (value) => isValidIPAMPoolId(value) }}
                                                        error={errors.Firewall?.EgressNAT?.Settings?.IPAMPoolId?.message}
                                                        dataMetrics="cloudngfw-firewall-create-egress-nat-public-own-ips"
                                                        disabled={disableIPPoolSettings}
                                                    />
                                                </div>
                                                {/* Show Sync button only if IPAM Pool ID is saved and feature flag is enabled */}
                                                {isIPAMPoolIdSaved && byoipSyncEnabled && (
                                                    <Button
                                                        onClick={handleSyncIPAMPool}
                                                        disabled={!props?.canEdit || firewallUpdating || !permissions?.UpdateFirewall || isSyncing}
                                                        appearance="primary"
                                                        dataMetrics="cloudngfw-firewall-edit-sync-ipam-pool"
                                                        style={{ marginTop: '24px' }}
                                                    >
                                                        {isSyncing ? (
                                                            <>
                                                                <SyncIcon size="sm" className="tw-animate-spin tw-mr-2" />
                                                                {translate('resources.firewallsV2.syncingIPAMPool')}
                                                            </>
                                                        ) : syncResult ? (
                                                            <>
                                                                <CircleCheckIcon size="sm" className="tw-mr-2" />
                                                                {translate('resources.firewallsV2.syncIPAMPoolCompleted')}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <SyncIcon size="sm" className="tw-mr-2" />
                                                                {translate('resources.firewallsV2.syncIPAMPool')}
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                            </div>

                                            {/* Helper text with AWS IP Address Manager link - only show if saved and feature flag is enabled */}
                                            {isIPAMPoolIdSaved && byoipSyncEnabled && (() => {
                                                const lastSyncTimeRaw = syncResult?.updatedDateTime || byoipSettings?.PublicIPPools?.[currentIPAMPoolId]?.LastSyncTimeUTC;
                                                const lastSyncTime = formatTimestamp(lastSyncTimeRaw);
                                                return (
                                                    <div style={{ maxWidth: '1000px' }} className="tw-text-sm tw-text-gray-600">
                                                        To add more IPs, please update in your{' '}
                                                        <Link
                                                            href={`https://${region}.console.aws.amazon.com/ipam/home?region=${region}#/ipam/pools`}
                                                            external
                                                        >
                                                            {translate('resources.firewallsV2.awsIPAddressManager')}
                                                        </Link>
                                                        {' '}first and then click "Sync" button.
                                                        {lastSyncTime && ` Last Updated Time: ${lastSyncTime}.`}
                                                    </div>
                                                );
                                            })()}

                                            {/* Success Banner - only show if saved and feature flag is enabled */}
                                            {isIPAMPoolIdSaved && byoipSyncEnabled && syncResult && (
                                                <div className="tw-mt-4">
                                                    <Banner
                                                        type="inline"
                                                        appearance="success"
                                                        showIcon
                                                        actions={
                                                            <div className="tw-flex tw-items-center tw-gap-4">
                                                                <Link
                                                                    href={`https://${region}.console.aws.amazon.com/ipam/home?region=${region}#/ipam/pools/${currentIPAMPoolId}`}
                                                                    external
                                                                    addClassName="tw-mr-2"
                                                                >
                                                                    {translate('resources.firewallsV2.goToIPAM')}
                                                                </Link>
                                                                <button
                                                                    onClick={() => setSyncResult(null)}
                                                                    className="tw-text-gray-500 hover:tw-text-gray-700"
                                                                    aria-label="Close"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        }
                                                    >
                                                        {syncResult.newIPs > 0
                                                            ? `Sync Successful: [${syncResult.newIPs}] new IPs added (Total: [${syncResult.totalIPs}]). To check the details, please go to your AWS IP Address Manager.`
                                                            : `IPAM Pool synced successfully (Total: [${syncResult.totalIPs}]). To check the details, please go to your AWS IP Address Manager.`}
                                                    </Banner>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {(() => {
                                        // Only show table if egress NAT was initially enabled (not stale data)
                                        if (!wasEgressNatInitiallyEnabled) {
                                            return null;
                                        }
                                        const publicIPs = formMethods.getValues("Status.PublicIPs") || [];
                                        const filteredIPs = publicIPs.filter((ip: any) => ip?.IPSource === iPoolType);
                                        return !isEmpty(filteredIPs) && (
                                            <AWSServiceIPsTableComponent
                                                value={filteredIPs}
                                            />
                                        );
                                    })()}
                                </>
                            )}
                            <div className={fwStyles.formFooterDivider}/>
                            <div className="tw-flex tw-justify-end">
                                <Button onClick={handleCancel} disabled={isEmpty(dirtyFields)} style={{ marginRight: '5px' }}>
                                    {translate(`generic.cancel`)}
                                </Button>
                                <SubmitButton type="submit" disabled={isEmpty(dirtyFields)}>
                                    {translate(`generic.save`)}
                                </SubmitButton>
                            </div>
                        </FormLayout>
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export default FirewallEgressNat;
